import attendancesApi from '@/api/attendance.api'
import type { SchoolGrade, Student } from '@/dto'
import { Attendance, AttendanceByStudentId } from '@/dto'
import type { Day } from '@/dto/common/Calendar/Day'

import { makeAdapter } from './Adapter'
import type Controller from './Controller'

class AttendanceController implements Controller {
  adapter = {
    attendance: makeAdapter({
      from: (data: res.Attendance) => {
        return new Attendance(data)
      },
    }),
    dateAttendanceList: makeAdapter({
      from: (data: res.DateAttendanceList[]) => {
        return new AttendanceByStudentId(data)
      },
    }),
  }

  async fetchAttendances(params: Omit<req.AttendanceParams, 'deduplicated'>) {
    const {
      data: { data },
    } = await attendancesApi.getAttendances({ ...params, deduplicated: false })

    return this.adapter.dateAttendanceList.from(data)
  }

  async fetchGradeAttendances(
    schoolType: SchoolGrade['schoolType'],
    grade: SchoolGrade['grade'],
    params: Omit<req.AttendanceParams, 'deduplicated'>,
  ) {
    const {
      data: { data },
    } = await attendancesApi.getAttendancesByGrade(schoolType, grade, {
      ...params,
      deduplicated: false,
    })

    return this.adapter.dateAttendanceList.from(data)
  }

  async fetchLessonClassAttendances(
    lessonClassId: number,
    params: Omit<req.AttendanceParams, 'deduplicated'>,
  ) {
    const {
      data: { data },
    } = await attendancesApi.getAttendancesByLessonClass(lessonClassId, {
      ...params,
      deduplicated: false,
    })

    return this.adapter.dateAttendanceList.from(data)
  }

  async fetchStudentAttendances(
    studentId: Student['id'],
    params: Omit<req.AttendanceParams, 'deduplicated'>,
  ) {
    const {
      data: { data },
    } = await attendancesApi.getAttendancesByStudent(studentId, { ...params, deduplicated: false })

    return this.adapter.dateAttendanceList.from(data)
  }

  async fetchSpecificDayAttendances(
    studentIdList: Student['id'][],
    day: Day,
    deduplicated = false,
  ) {
    const {
      data: { data },
    } = await attendancesApi.getAttendances({
      startDate: day.toString,
      endDate: day.toString,
      deduplicated,
    })

    return data
  }

  async enterAttendance(params: req.AttendanceEnter) {
    const {
      data: { data },
    } = await attendancesApi.putAttendanceEnter(params)

    return data.map(this.adapter.attendance.from)
  }

  async quitAttendance(params: req.AttendanceQuit) {
    const {
      data: { data },
    } = await attendancesApi.putAttendanceQuit(params)

    return data.map(this.adapter.attendance.from)
  }

  async deleteAttendances(ids: Attendance['id'][]) {
    await attendancesApi.deleteAttendances(ids)
  }

  async getXLS(params: req.AttendanceXLSParams) {
    return await attendancesApi.getXLS(params)
  }
}

const attendanceController = new AttendanceController()

export default attendanceController
